<script lang="ts">
	import { Icon, LinearGauge, Text } from '@Shelly/shelly-design-system';
	import { convert } from 'src/helpers/utilities';
	import type { PhasePeakBalanceModel } from '../helpers/energy_peak_helpers';
	import EmPeakDeviceAvatar from './EmPeakDeviceAvatar.svelte';

	export let model: PhasePeakBalanceModel;

	const timeOnly = (value: string | undefined): string => value?.split(' ')[0] || '—';
	const formatWatts = (watts: number): string => convert(Math.abs(watts), 'W');
</script>

<div class="em-phase-balance">
	<div class="em-phase-balance-summary">
		<div class="em-phase-balance-summary-item em-phase-balance-summary-peak">
			<Text styleType="h1" class="text-600">{model.strongest.live?.value ?? '—'}</Text>
			<Text styleType="body-small" class="text-color-muted">Highest phase</Text>
			<Text styleType="body-small" class="text-color-muted">
				{model.panel.deviceName} · {model.strongest.label} · {timeOnly(
					model.strongest.live?.time,
				)}
			</Text>
		</div>
		<div class="em-phase-balance-summary-item">
			<div
				class:em-phase-balance-status-ok={model.isBalanced}
				class:em-phase-balance-status-warning={!model.isBalanced}
			>
				<Icon
					icon={model.isBalanced ? 'fas fa-circle-check' : 'fas fa-triangle-exclamation'}
					size="s"
				/>
				<Text styleType="h2" class="text-600">
					{model.isBalanced ? 'Balanced' : 'Imbalance'}
				</Text>
			</div>
			<Text styleType="body-small" class="text-color-muted">{model.spreadPercent}% spread</Text>
		</div>
		<div class="em-phase-balance-summary-item em-phase-balance-summary-peak">
			<Text styleType="h1" class="text-600">{model.strongest.avg5?.value ?? '—'}</Text>
			<Text styleType="body-small" class="text-color-muted">Highest 5 min avg</Text>
			<Text styleType="body-small" class="text-color-muted">
				{model.strongest.label} · {timeOnly(model.strongest.avg5?.time)}
			</Text>
		</div>
	</div>

	<div class="em-phase-balance-list">
		{#each model.phases as phase (phase.channel)}
			<div
				class="em-phase-balance-row"
				class:em-phase-balance-row-strongest={phase.channel === model.strongest.channel}
			>
				<div class="em-phase-balance-source">
					<EmPeakDeviceAvatar device={model.panel.device} size="m" />
					<Text styleType="h2" class="text-600">{phase.label}</Text>
				</div>
				<div class="em-phase-balance-metric">
					<Text styleType="miniature" class="text-color-muted">Live peak</Text>
					<Text styleType="h2" class="text-600">{phase.live?.value ?? '—'}</Text>
				</div>
				<div class="em-phase-balance-metric">
					<Text styleType="miniature" class="text-color-muted">Avg 5 min</Text>
					<Text styleType="h2" class="text-600">{phase.avg5?.value ?? '—'}</Text>
				</div>
				<Text styleType="body-small" class="text-color-muted">
					{timeOnly(phase.live?.time)}
				</Text>
				<div class="em-phase-balance-gauge">
					<LinearGauge
						type="incremented"
						size="s"
						minValue={0}
						maxValue={model.strongest.liveWatts || 1}
						hideMinMaxValue
						incrementedValue={{
							value: phase.liveWatts,
							color:
								phase.channel === model.strongest.channel
									? '--color-warning'
									: '--color-primary',
						}}
						a11yProps={{
							incrementedValueAriaLabel: `${phase.label} ${phase.live?.value ?? ''}`,
						}}
					/>
					<Text
						styleType="body"
						class={phase.channel === model.strongest.channel
							? 'em-phase-balance-percent-strongest'
							: 'text-color-muted'}
					>
						{Math.round((phase.liveWatts / (model.strongest.liveWatts || 1)) * 100)}%
					</Text>
				</div>
			</div>
		{/each}
	</div>

	<div class="em-phase-balance-marker">
		<span></span>
		<Text styleType="body-small" class="text-color-muted">Avg 5 min values shown per phase</Text>
	</div>

	<div class="em-phase-balance-footer">
		{#each model.pairSpreads.slice(0, 2) as spread (spread.id)}
			<div>
				<Icon icon="fal fa-scale-balanced" size="s" />
				<Text styleType="body-small" class="text-color-muted">
					{spread.label} spread
				</Text>
				<Text styleType="body" class="text-600">{formatWatts(spread.watts)}</Text>
			</div>
		{/each}
		<div>
			<Icon icon="fal fa-circle-dot" size="s" />
			<Text styleType="body-small" class="text-color-muted">
				{model.phases.length} phases reporting
			</Text>
		</div>
	</div>
</div>

<style>
	.em-phase-balance {
		display: flex;
		flex-direction: column;
		min-width: 0;
		container-type: inline-size;
		container-name: phase-balance;
	}

	.em-phase-balance-summary {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-phase-balance-summary-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-width: 0;
		padding: var(--spacing-m);
		text-align: center;
	}

	.em-phase-balance-summary-item + .em-phase-balance-summary-item {
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-phase-balance-summary-peak {
		color: var(--color-warning);
	}

	.em-phase-balance-status-ok,
	.em-phase-balance-status-warning {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
	}

	.em-phase-balance-status-ok {
		color: var(--color-success);
	}

	.em-phase-balance-status-warning,
	:global(.em-phase-balance-percent-strongest) {
		color: var(--color-warning);
	}

	.em-phase-balance-list {
		display: flex;
		flex-direction: column;
	}

	.em-phase-balance-row {
		position: relative;
		display: grid;
		grid-template-columns: minmax(130px, 1fr) auto auto auto minmax(180px, 1.6fr);
		align-items: center;
		gap: var(--spacing-m);
		min-width: 0;
		padding: var(--spacing-s) var(--spacing-m);
	}

	.em-phase-balance-row + .em-phase-balance-row {
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-phase-balance-row-strongest {
		background: color-mix(in srgb, var(--color-warning) 7%, transparent);
	}

	.em-phase-balance-source,
	.em-phase-balance-gauge,
	.em-phase-balance-footer > div,
	.em-phase-balance-marker {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
		min-width: 0;
	}

	.em-phase-balance-metric {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.em-phase-balance-gauge :global(.linear-gauge) {
		flex: 1;
	}

	.em-phase-balance-marker {
		justify-content: center;
		padding: var(--spacing-s);
	}

	.em-phase-balance-marker span {
		width: var(--spacing-xxs);
		height: var(--spacing-m);
		background: var(--color-white);
	}

	.em-phase-balance-footer {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: var(--spacing-m);
		margin: 0 var(--spacing-m) var(--spacing-m);
		padding: var(--spacing-s) var(--spacing-m);
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-phase-balance-footer > div + div {
		padding-left: var(--spacing-m);
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	@container phase-balance (max-width: 560px) {
		.em-phase-balance-summary {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.em-phase-balance-summary-item:first-child {
			grid-column: 1 / -1;
		}

		.em-phase-balance-summary-item:nth-child(n + 2) {
			border-top: 1px solid rgba(255, 255, 255, 0.12);
			border-left: 0;
		}

		.em-phase-balance-summary-item:nth-child(3) {
			border-left: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-phase-balance-row {
			grid-template-columns: minmax(0, 1fr) auto auto;
			gap: var(--spacing-s);
		}

		.em-phase-balance-gauge {
			grid-column: 1 / -1;
		}

		.em-phase-balance-row > :nth-child(4) {
			display: none;
		}

		.em-phase-balance-footer {
			grid-template-columns: minmax(0, 1fr);
		}

		.em-phase-balance-footer > div + div {
			padding-top: var(--spacing-s);
			padding-left: 0;
			border-top: 1px solid rgba(255, 255, 255, 0.12);
			border-left: 0;
		}
	}

	@container phase-balance (max-width: 360px) {
		.em-phase-balance-row {
			grid-template-columns: minmax(0, 1fr) repeat(2, minmax(52px, auto));
		}

		.em-phase-balance-summary {
			grid-template-columns: minmax(0, 1fr);
		}

		.em-phase-balance-summary-item:first-child {
			grid-column: auto;
		}

		.em-phase-balance-summary-item:nth-child(3) {
			border-left: 0;
		}

		.em-phase-balance-source :global(.label) {
			display: none;
		}
	}
</style>
